import jsPDF from 'jspdf';
import type { Tables } from '@/types/database.types';

type MilestoneRow = Tables<'milestones'>;
type RiskRow = Tables<'risks'>;
type KpiRow = Tables<'kpis'>;
type TaskRow = Tables<'tasks'>;
type StatusUpdateRow = Tables<'status_updates'>;

interface ProjectExportData {
  name: string;
  trackerId: string | null;
  description: string;
  overallHealth: 'green' | 'yellow' | 'red';
  startDate: string | null;
  endDate: string | null;
  completionPercentage: number | null;
  executiveSummary: string | null;
  milestones: MilestoneRow[];
  risks: RiskRow[];
  kpis: KpiRow[];
  tasks: TaskRow[];
  accomplishments: StatusUpdateRow[];
  priorities: StatusUpdateRow[];
}

type RGB = [number, number, number];

// Printer-friendly palette. The Basic export is intentionally monochrome —
// no filled backgrounds, no banners. Color appears ONLY as tinted text for
// status / severity / priority words; everything else is black or gray.
const INK: RGB = [17, 24, 39];        // near-black body text
const MUTED: RGB = [107, 114, 128];   // gray meta / labels
const HAIRLINE: RGB = [209, 213, 219]; // light gray rules

// Accent *text* colors (never used as fills).
const ACCENT = {
  green: [22, 163, 74] as RGB,
  amber: [202, 138, 4] as RGB,
  red: [220, 38, 38] as RGB,
  orange: [234, 88, 12] as RGB,
  blue: [37, 99, 235] as RGB,
  gray: [107, 114, 128] as RGB,
};

const healthConfig: Record<string, { label: string; color: RGB }> = {
  green: { label: 'On Track', color: ACCENT.green },
  yellow: { label: 'At Risk', color: ACCENT.amber },
  red: { label: 'Off Track', color: ACCENT.red },
};

const severityColor: Record<string, RGB> = {
  low: ACCENT.gray,
  medium: ACCENT.amber,
  high: ACCENT.orange,
  critical: ACCENT.red,
};

const milestoneStatusColor: Record<string, RGB> = {
  completed: ACCENT.green,
  in_progress: ACCENT.blue,
  not_started: MUTED,
};

const taskStatusColor: Record<string, RGB> = {
  todo: MUTED,
  in_progress: ACCENT.blue,
  complete: ACCENT.green,
  cancelled: MUTED,
};

const priorityColor: Record<string, RGB> = {
  low: ACCENT.gray,
  medium: ACCENT.amber,
  high: ACCENT.orange,
  urgent: ACCENT.red,
};

// Task status groups rendered in the Tasks section. Cancelled is omitted.
const TASK_GROUPS: { key: string; label: string }[] = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'complete', label: 'Complete' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function capitalize(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function exportProjectPdf(data: ProjectExportData) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkPageBreak = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // --- Title block (no banner, plain text on white) ---
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  const titleLines = doc.splitTextToSize(data.name, contentWidth);
  doc.text(titleLines, margin, y + 6);
  y += 6 + (titleLines.length - 1) * 9 + 6;

  // Meta line: tracker · date range · generated
  const metaParts = [
    data.trackerId || null,
    (data.startDate || data.endDate) ? `${formatDate(data.startDate)} – ${formatDate(data.endDate)}` : null,
    `Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
  ].filter(Boolean) as string[];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(metaParts.join('   ·   '), margin, y);
  y += 4;

  // Full-width hairline under the title block
  hairline(doc, margin, y, contentWidth);
  y += 8;

  // --- Description ---
  if (data.description) {
    doc.setTextColor(...INK);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(data.description, contentWidth);
    checkPageBreak(lines.length * 4.6 + 6);
    doc.text(lines, margin, y);
    y += lines.length * 4.6 + 6;
  }

  // --- Overview (label/value stats, no card fills) ---
  {
    const health = healthConfig[data.overallHealth];
    const completedMs = data.milestones.filter(m => m.status === 'completed').length;
    const openTasks = data.tasks.filter(t => t.status !== 'complete' && t.status !== 'cancelled').length;
    const openRisks = data.risks.filter(r => (r.status || 'open') !== 'closed').length;

    const stats: { label: string; value: string; color?: RGB }[] = [
      { label: 'Health', value: health.label, color: health.color },
      { label: 'Completion', value: data.completionPercentage !== null ? `${data.completionPercentage}%` : 'N/A' },
      { label: 'Milestones', value: `${completedMs} / ${data.milestones.length} complete` },
      { label: 'Timeline', value: `${formatDate(data.startDate)} – ${formatDate(data.endDate)}` },
      { label: 'Open Tasks', value: `${openTasks} of ${data.tasks.length}` },
      { label: 'Open Risks', value: `${openRisks}` },
    ];

    const cols = 3;
    const colW = contentWidth / cols;
    const rowH = 13;
    const rows = Math.ceil(stats.length / cols);
    checkPageBreak(rows * rowH + 4);
    stats.forEach((s, i) => {
      const cx = margin + (i % cols) * colW;
      const cy = y + Math.floor(i / cols) * rowH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...MUTED);
      doc.text(s.label.toUpperCase(), cx, cy + 3.5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...(s.color ?? INK));
      doc.text(s.value, cx, cy + 9.5);
    });
    y += rows * rowH + 4;
  }

  // --- Executive Summary ---
  if (data.executiveSummary) {
    y = drawSectionHeader(doc, 'Executive Summary', margin, y, contentWidth, checkPageBreak);
    doc.setTextColor(...INK);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(data.executiveSummary, contentWidth);
    checkPageBreak(lines.length * 4.6);
    doc.text(lines, margin, y);
    y += lines.length * 4.6 + 6;
  }

  // --- Milestones ---
  if (data.milestones.length > 0) {
    y = drawSectionHeader(doc, 'Milestones', margin, y, contentWidth, checkPageBreak);

    const showOwner = data.milestones.some(m => !!m.owner_name);
    const colWidths = showOwner
      ? [contentWidth * 0.40, contentWidth * 0.20, contentWidth * 0.20, contentWidth * 0.20]
      : [contentWidth * 0.52, contentWidth * 0.24, contentWidth * 0.24];
    const headers = showOwner
      ? ['Milestone', 'Due Date', 'Status', 'Owner']
      : ['Milestone', 'Due Date', 'Status'];

    y = drawTableHeader(doc, headers, colWidths, margin, y, contentWidth, checkPageBreak);

    data.milestones.forEach((m) => {
      checkPageBreak(9);
      const nameLines = doc.splitTextToSize(m.name, colWidths[0] - 3);
      const rowH = Math.max(7, nameLines.length * 4 + 3);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...INK);
      let rx = margin;
      doc.text(nameLines, rx, y + 4);
      rx += colWidths[0];
      doc.setTextColor(...MUTED);
      doc.text(formatDate(m.due_date), rx, y + 4);
      rx += colWidths[1];
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(milestoneStatusColor[m.status || 'not_started'] ?? MUTED));
      doc.text(capitalize(m.status || 'not_started'), rx, y + 4);
      rx += colWidths[2];
      if (showOwner) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...MUTED);
        doc.text(m.owner_name || '—', rx, y + 4);
      }
      y += rowH;
      hairline(doc, margin, y, contentWidth);
    });
    y += 6;
  }

  // --- Priorities ---
  if (data.priorities.length > 0) {
    y = drawSectionHeader(doc, 'Priorities', margin, y, contentWidth, checkPageBreak);
    y = drawBulletList(doc, data.priorities.map(p => p.content), margin, y, contentWidth, checkPageBreak);
    y += 4;
  }

  // --- Risks ---
  if (data.risks.length > 0) {
    y = drawSectionHeader(doc, 'Risks', margin, y, contentWidth, checkPageBreak);

    data.risks.forEach((risk, idx) => {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const summaryLines = doc.splitTextToSize(risk.summary, contentWidth);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      const descLines = risk.description ? doc.splitTextToSize(risk.description, contentWidth) : [];
      const mitigationLines = risk.mitigation_plan ? doc.splitTextToSize(`Mitigation: ${risk.mitigation_plan}`, contentWidth) : [];
      const blockH = summaryLines.length * 4.5 + 5 + descLines.length * 3.8 + (descLines.length ? 2 : 0) + mitigationLines.length * 3.8 + 6;
      checkPageBreak(blockH);

      if (idx > 0) y += 1;

      // Summary
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...INK);
      doc.text(summaryLines, margin, y + 3);
      y += summaryLines.length * 4.5 + 1;

      // Meta line — severity tinted, status + owner muted
      const sevColor = severityColor[risk.severity] || ACCENT.amber;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...sevColor);
      doc.text(capitalize(risk.severity), margin, y + 3);
      const sevW = doc.getTextWidth(capitalize(risk.severity));
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED);
      const metaRest = `   ·   ${capitalize(risk.status || 'open')}${risk.owner_name ? `   ·   Owner: ${risk.owner_name}` : ''}`;
      doc.text(metaRest, margin + sevW, y + 3);
      y += 5;

      // Description
      if (descLines.length > 0) {
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...INK);
        doc.text(descLines, margin, y + 1);
        y += descLines.length * 3.8 + 2;
      }
      // Mitigation
      if (mitigationLines.length > 0) {
        doc.setFontSize(8.5);
        doc.setTextColor(...MUTED);
        doc.text(mitigationLines, margin, y + 1);
        y += mitigationLines.length * 3.8;
      }
      y += 4;
      hairline(doc, margin, y, contentWidth);
      y += 3;
    });
    y += 3;
  }

  // --- Accomplishments ---
  if (data.accomplishments.length > 0) {
    y = drawSectionHeader(doc, 'Accomplishments', margin, y, contentWidth, checkPageBreak);
    y = drawBulletList(doc, data.accomplishments.map(a => a.content), margin, y, contentWidth, checkPageBreak);
    y += 4;
  }

  // --- KPIs ---
  if (data.kpis.length > 0) {
    y = drawSectionHeader(doc, 'KPIs', margin, y, contentWidth, checkPageBreak);
    const colWidths = [contentWidth * 0.40, contentWidth * 0.20, contentWidth * 0.20, contentWidth * 0.20];
    const headers = ['KPI', 'Current', 'Target', 'Progress'];
    y = drawTableHeader(doc, headers, colWidths, margin, y, contentWidth, checkPageBreak);

    const fmtVal = (n: number | null, unit: string | null) => n === null ? '—' : `${n}${unit ? ' ' + unit : ''}`;

    data.kpis.forEach((kpi) => {
      checkPageBreak(9);
      const nameLines = doc.splitTextToSize(kpi.name, colWidths[0] - 3);
      const rowH = Math.max(7, nameLines.length * 4 + 3);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...INK);
      let rx = margin;
      doc.text(nameLines, rx, y + 4);
      rx += colWidths[0];
      doc.setTextColor(...MUTED);
      doc.text(fmtVal(kpi.current_value, kpi.unit), rx, y + 4);
      rx += colWidths[1];
      doc.text(fmtVal(kpi.target, kpi.unit), rx, y + 4);
      rx += colWidths[2];
      if (kpi.target !== null && kpi.target !== 0) {
        const pct = Math.max(0, Math.min(100, ((kpi.current_value ?? 0) / kpi.target) * 100));
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...INK);
        doc.text(`${Math.round(pct)}%`, rx, y + 4);
      } else {
        doc.text('—', rx, y + 4);
      }
      y += rowH;
      hairline(doc, margin, y, contentWidth);
    });
    y += 6;
  }

  // --- Tasks (grouped by status; cancelled omitted) ---
  const taskable = data.tasks.filter(t => t.status !== 'cancelled');
  if (taskable.length > 0) {
    y = drawSectionHeader(doc, 'Tasks', margin, y, contentWidth, checkPageBreak);

    const completed = taskable.filter(t => t.status === 'complete').length;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(`${completed} of ${taskable.length} complete`, margin, y);
    y += 6;

    TASK_GROUPS.forEach(({ key, label }) => {
      const group = taskable.filter(t => t.status === key);
      if (group.length === 0) return;
      checkPageBreak(10);
      // Group subheading
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(taskStatusColor[key] ?? MUTED));
      doc.text(`${label} (${group.length})`, margin, y);
      y += 5;

      group.forEach((t) => {
        const titleLines = doc.splitTextToSize(t.title, contentWidth - 6);
        const metaBits = [
          capitalize(t.priority),
          t.due_date ? formatDate(t.due_date) : null,
          t.assigned_to || null,
        ].filter(Boolean) as string[];
        const rowH = titleLines.length * 4 + (metaBits.length ? 3.6 : 0) + 3;
        checkPageBreak(rowH);

        // Bullet + title
        doc.setFillColor(...INK);
        doc.circle(margin + 1, y + 1.6, 0.7, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...INK);
        doc.text(titleLines, margin + 6, y + 2.5);
        let my = y + 2.5 + (titleLines.length - 1) * 4 + 3.6;

        // Meta: priority tinted, rest muted
        if (metaBits.length) {
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...(priorityColor[t.priority] ?? MUTED));
          doc.text(capitalize(t.priority), margin + 6, my);
          const pw = doc.getTextWidth(capitalize(t.priority));
          const rest = metaBits.slice(1);
          if (rest.length) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...MUTED);
            doc.text(`   ·   ${rest.join('   ·   ')}`, margin + 6 + pw, my);
          }
          my += 1;
        }
        y = my + 2.5;
      });
      y += 3;
    });
    y += 2;
  }

  // --- Footer (page numbers) ---
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(`${data.name} — Project Report`, margin, pageHeight - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  const filename = `${data.name.replace(/[^a-zA-Z0-9]/g, '_')}_Report.pdf`;
  doc.save(filename);
}

// --- Helpers ---

function hairline(doc: jsPDF, x: number, y: number, w: number) {
  doc.setDrawColor(...HAIRLINE);
  doc.setLineWidth(0.2);
  doc.line(x, y, x + w, y);
}

function drawSectionHeader(
  doc: jsPDF, title: string, x: number, y: number, w: number,
  checkPageBreak: (needed: number) => void,
): number {
  checkPageBreak(16);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text(title, x, y + 4);
  hairline(doc, x, y + 6.5, w);
  return y + 12;
}

function drawTableHeader(
  doc: jsPDF, headers: string[], colWidths: number[], x: number, y: number, w: number,
  checkPageBreak: (needed: number) => void,
): number {
  checkPageBreak(10);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...MUTED);
  let cx = x;
  headers.forEach((h, i) => {
    doc.text(h.toUpperCase(), cx, y + 3);
    cx += colWidths[i];
  });
  y += 5;
  hairline(doc, x, y, w);
  return y + 2;
}

function drawBulletList(
  doc: jsPDF, items: string[], x: number, y: number, w: number,
  checkPageBreak: (needed: number) => void,
): number {
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  items.forEach(content => {
    const lines = doc.splitTextToSize(content, w - 6);
    const blockH = lines.length * 4.2 + 1.5;
    checkPageBreak(blockH);
    doc.setFillColor(...INK);
    doc.circle(x + 1, y + 1.6, 0.7, 'F');
    doc.setTextColor(...INK);
    doc.text(lines, x + 6, y + 2.5);
    y += blockH;
  });
  return y;
}
